import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Grid, Paper, Theme, alpha } from '@mui/material'
import { makeStyles } from '@mui/styles'

import PostCalendar from './PostCalendar'
import PostCalendarNav from './PostCalendarNav'

import { PostTypeEnum } from '@professocial/professocialTypes/types/commonTypes'

import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { getTheme, getUser } from '../../../../store/common/common.selectors'
import {
  getCalendarDataInitiated,
  getTopicsAndMetadataForCurrentUserInitiated,
  getAboSettingsForCurrentUserInitiated,
} from '../../../../store/dashboard/dashboard.actions'
import { GetCalendarDataRequest } from '@3m5/sm_publish-frontend-api'
import {
  getAboTopics,
  getCalendarData,
  getPostingStatus,
  getReloadCalendar,
} from '../../../../store/dashboard/dashboard.selector'
import {
  CalendarData,
  DeferredPost, NetworkType,
  PostContent,
  PostTemplate,
  PublishedPostWithPostContent, TopicsWithMetadataResponse,
} from '@3m5/sm_publish-frontend-api'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import { PostingStatus } from '@professocial/professocialPosting/src/shared/types/types'
import { TopicWithMetadata } from '@3m5/sm_publish-frontend-api'

export interface CalendarDateParams {
  from: Date;
  to: Date;
}

export interface PostObject {
  published: PublishedPostWithPostContent[];
  deferred: DeferredPost[];
  postContent?: PostContent;
  template?: PostTemplate;
}

export interface TemplateObject {
  [key: string]: PostObject;
}

export interface DateObject {
  [key: string]: TemplateObject;
}

interface CalendarFilter {
  topics: DropDownElement | null;
  networks: DropDownElement | null;
  types: DropDownElement | null;
  display: DropDownElement[];
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(5),
  },
}))

export enum CalendarPostSource {
  deferredPost = 'deferredPost',
  publishedPost = 'publishedPost',
  template = 'template',
}

export enum CalendarDisplayFilter {
  subscribedPosts = 'subscribedPosts',
  ownPosts = 'ownPosts',
  morePosts = 'morePosts',
}

const ProfessocialCalendar: React.FC = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme: Theme = useSelector(getTheme)
  const user = useSelector(getUser)

  const dispatch = useDispatch()
  const dispatchGetCalendarData = useCallback((params: GetCalendarDataRequest) => dispatch(getCalendarDataInitiated(params)), [dispatch])
  const dispatchGetTopics = useCallback(() => dispatch(getTopicsAndMetadataForCurrentUserInitiated()), [dispatch])
  const dispatchGetAboSettings = useCallback(() => dispatch(getAboSettingsForCurrentUserInitiated()), [dispatch])

  const calendarResponseData: CalendarData | undefined = useSelector(getCalendarData)
  const topicResponseData: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)
  const postingStatus = useSelector(getPostingStatus)
  const reloadCalendar: boolean = useSelector(getReloadCalendar)

  const displayFilterData: DropDownElement[] = [
    {
      key: CalendarDisplayFilter.subscribedPosts,
      value: t('coreCalendar:subscribedPosts'),
    },
    {
      key: CalendarDisplayFilter.ownPosts,
      value: t('coreCalendar:ownPosts'),
      color: alpha(theme.palette.primary.main, 0.15),
    },
    {
      key: CalendarDisplayFilter.morePosts,
      value: t('coreCalendar:morePosts'),
    },
  ]
  const [topicData, setTopicData] = React.useState<DropDownElement[]>()
  const [networkData, setNetworkData] = React.useState<DropDownElement[]>()
  const [postingTypeData, setPostTypeEnumData] = React.useState<DropDownElement[]>()

  const [showOwnPosts, setShowOwnPosts] = React.useState<boolean>(true)
  const [showSubscribedPosts, setShowSubscribedPosts] = React.useState<boolean>(true)
  const [showMorePosts, setShowMorePosts] = React.useState<boolean>(false)

  const [calendarData, setCalendarData] = React.useState<DateObject>()
  const [selectedDate, setSelectedDate] = React.useState<number>(Date.now())
  const [calendarDateParams, setCalendarDateParams] = React.useState<CalendarDateParams>()
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<CalendarFilter>({
    topics: null,
    networks: null,
    types: null,
    display: [{
      key: CalendarDisplayFilter.subscribedPosts,
      value: t('coreCalendar:subscribedPosts'),
    },
    {
      key: CalendarDisplayFilter.ownPosts,
      value: t('coreCalendar:ownPosts'),
      color: alpha(theme.palette.primary.main, 0.15),
    }],
  })

  useEffect(() => {
    const subscribed = filter.display.find(elem => elem.key === CalendarDisplayFilter.subscribedPosts)
    const own = filter.display.find(elem => elem.key === CalendarDisplayFilter.ownPosts)
    const more = filter.display.find(elem => elem.key === CalendarDisplayFilter.morePosts)
    setShowSubscribedPosts(!!subscribed)
    setShowOwnPosts(!!own)
    setShowMorePosts(!!more)
  }, [filter])

  useEffect(() => {
    if (reloadCalendar) {
      setTimeout(() => {
        calendarDateParams && dispatchGetCalendarData({ start: calendarDateParams.from, end: calendarDateParams.to })
      }, 1000)
    }
  }, [reloadCalendar])

  useEffect(() => {
    if (postingStatus === PostingStatus.success) {
      setTimeout(() => {
        calendarDateParams && dispatchGetCalendarData({ start: calendarDateParams.from, end: calendarDateParams.to })
      }, 1000)
    }
  }, [postingStatus])

  const addPostType = (array: DropDownElement[], postContent?: PostContent) => {
    const postingTypes: DropDownElement[] = array
    if (postContent) {
      let typeConfig = { type: PostTypeEnum.MESSAGE, color: '#E99425' }
      if (postContent.image?.url) {
        typeConfig = { type: PostTypeEnum.PHOTO, color: '#258EE9' }
      }
      if (postContent.video?.url) {
        typeConfig = { type: PostTypeEnum.VIDEO, color: '#258EE9' }
      }
      if (postContent.link?.url) {
        typeConfig = { type: PostTypeEnum.LINK, color: '#E92525' }
      }

      const existingPostTypeEnum = postingTypes.find((elem) => {
        if (elem.key === typeConfig.type) {
          return elem
        }
      })
      const label = t(`coreCalendar:${typeConfig.type}`)
      !existingPostTypeEnum && postingTypes.push({ key: typeConfig.type, value: label, color: typeConfig.color })
    }
    return postingTypes
  }

  const addNetworkType = (array: DropDownElement[], network: NetworkType) => {
    const networks: DropDownElement[] = array
    if (array.findIndex((elem) => elem.key === network) === -1) {
      networks.push({ key: network, value: network })
    }
    return networks
  }

  const extractFilterData = (data: DateObject) => {
    const topics: DropDownElement[] = []
    let networks: DropDownElement[] = []
    let postingTypes: DropDownElement[] = []

    let existingTopicIDs: string[] = []

    Object.keys(data).map(dateKey => {
      Object.keys(data[dateKey]).map(templateKey => {
        // topics filtern
        const template = calendarResponseData?.templates.find(elem => elem.id === templateKey)
        if (template) {
          template.topicId && existingTopicIDs.push(template.topicId)
        }
        // postType / networkType filten
        data[dateKey][templateKey].published.map((published) => {
          postingTypes = addPostType(postingTypes, published.postContent)
          networks = addNetworkType(networks, published.publishedPost.networkType)
        })
        data[dateKey][templateKey].deferred.map((deferred) => {
          postingTypes = addPostType(postingTypes, deferred.overridingPostContent)
          networks = addNetworkType(networks, deferred.networkType)
        })
        const temp: PostTemplate | undefined = data[dateKey][templateKey].template
        if (temp) {
          postingTypes = addPostType(postingTypes, temp.postContent)
          temp.allowedNetworks.map(network => {
            networks = addNetworkType(networks, network)
          })
        }
      })
    })

    // Topics mappen
    existingTopicIDs = existingTopicIDs.filter((x, i) => existingTopicIDs.indexOf(x) === i)
    if (topicResponseData && existingTopicIDs) {
      topicResponseData.topics.filter(({ topic }) => existingTopicIDs?.some(item => item === topic.id)).map(({ topic }) => {
        topic.id && topics.push({ key: topic.id, value: topic.name })
      })
    }
    setTopicData(topics)
    setNetworkData(networks)
    setPostTypeEnumData(postingTypes)
  }

  const handleSetFilterOption = (option: DropDownElement | DropDownElement[], key: string) => {
    if (!Array.isArray(option) && option !== null) {
      setFilter({
        ...filter,
        [key]: { key: option.key, value: option.value },
      })
    } else {
      const subscribe = option && option.find(elem => elem.key === CalendarDisplayFilter.subscribedPosts || elem.key === CalendarDisplayFilter.morePosts)
      if (key === 'display' && !subscribe) {
        setFilter({
          ...filter,
          [key]: option,
          topics: null,
        })
      } else {
        setFilter({
          ...filter,
          [key]: option,
        })
      }
    }
  }

  const getPostType = (postContent: PostContent) => {
    let postType: PostTypeEnum = PostTypeEnum.MESSAGE
    if (postContent.image?.url) {
      postType = PostTypeEnum.PHOTO
    }
    if (postContent.video?.url) {
      postType = PostTypeEnum.VIDEO
    }
    if (postContent.link?.url) {
      postType = PostTypeEnum.LINK
    }
    return postType
  }

  const filterDateObjects = () => {
    const dateObject: DateObject = {}
    // 1. Gruppieren aller publishedPosts nach Datum
    calendarResponseData?.publishedPosts.map((published) => {
      if (!published.publishedPost.deleted) {
        const dateString: string = formatDate(new Date(published.publishedPost.postedAt).getTime(), 'dd.MM.yyyy')
        const templateId: string | undefined = published.publishedPost.templateId
        if (!dateObject[dateString]) {
          dateObject[dateString] = {
            freePosts: {
              published: [],
              deferred: [],
            },
          }
        }

        // Filter displayType
        if ((showOwnPosts && !templateId) || (showSubscribedPosts && templateId)) {
          // Filter nach Types
          if ((filter.types && published.postContent && getPostType(published.postContent) === filter.types.key) || !filter.types) {
            // Filter nach NetworkTypes
            if ((filter.networks && published.publishedPost.networkType === filter.networks.key) || !filter.networks) {
              // Filter nach Topics
              const temp: PostTemplate | undefined = calendarResponseData?.templates.find((template) => template.id === published.publishedPost.templateId)
              if ((filter.topics && temp?.topicId === filter.topics.key) || !filter.topics) {
                if (templateId) {
                  if (!dateObject[dateString][templateId]) {
                    dateObject[dateString][templateId] =
                      {
                        published: [],
                        deferred: [],
                      }
                  }
                  dateObject[dateString][templateId].published.push(published)
                  dateObject[dateString][templateId].postContent = calendarResponseData?.templates.find(elem => elem.id === published.publishedPost.templateId)?.postContent
                  dateObject[dateString][templateId].template = temp
                  // prüfen ob es weitere deferredPosts mit dieser TemplateId gibt
                  const tempDef: DeferredPost | undefined = calendarResponseData?.deferredPosts.find(def => def.templateId === templateId)
                  tempDef && dateObject[dateString][templateId].deferred.push(tempDef)
                } else {
                  dateObject[dateString].freePosts.published.push(published)
                }
              }
            }
          }
        }
      }
    })

    // 2. Gruppieren aller deferredPosts nach Datum
    calendarResponseData?.deferredPosts.map((deferred: DeferredPost) => {
      if (deferred.postStart && deferred.postStart > new Date()) {
        const dateString: string = deferred.postStart && formatDate(new Date(deferred.postStart).getTime(), 'dd.MM.yyyy')
        const templateId: string | undefined = deferred.templateId
        if (!dateObject[dateString]) {
          dateObject[dateString] = {
            freePosts: {
              published: [],
              deferred: [],
            },
          }
        }
        // Filter displayType
        if ((showOwnPosts && !templateId) || (showSubscribedPosts && templateId)) {
          // Filter nach Types
          if ((filter.types && deferred.overridingPostContent && getPostType(deferred.overridingPostContent) === filter.types.key) || !filter.types) {
            // Filter nach NetworkTypes
            if ((filter.networks && deferred.networkType === filter.networks.key) || !filter.networks) {
              // Filter nach Topics
              const temp: PostTemplate | undefined = calendarResponseData?.templates.find((template) => template.id === deferred.templateId)
              if ((filter.topics && temp?.topicId === filter.topics.key) || !filter.topics) {
                if (templateId) {
                  if (!dateObject[dateString][templateId]) {
                    dateObject[dateString][templateId] = {
                      published: [],
                      deferred: [],
                    }
                  }
                  dateObject[dateString][templateId].deferred.push(deferred)
                  dateObject[dateString][templateId].postContent = calendarResponseData?.templates.find(elem => elem.id === deferred.templateId)?.postContent
                  dateObject[dateString][templateId].template = temp
                  // prüfen ob es weitere deferredPosts mit dieser TemplateId an diesem Tag gibt
                  const tempDef: DeferredPost | undefined = calendarResponseData?.deferredPosts.find(def => def.templateId === templateId && def.postStart === deferred.postStart)
                  tempDef && dateObject[dateString][templateId].deferred.push(tempDef)
                } else {
                  dateObject[dateString].freePosts.deferred.push(deferred)
                }
              }
            }
          }
        }
      }
    })
    // 3. Gruppieren aller templates, sofern noch nicht verwendet, abonnierbar und in Zukunft
    calendarResponseData?.templates.map((template: PostTemplate) => {
      const topic: TopicWithMetadata | undefined = topicResponseData?.topics.find(elem => elem.topic.id === template.topicId)
      if ((template.postStart && template.postStart > new Date() && topic?.topic.active) || (template.postStart && user?.role === 'EDITOR')) {
        const dateString: string = template.postStart && formatDate(new Date(template.postStart).getTime(), 'dd.MM.yyyy')
        const templateId: string = template.id

        // abonnierte und nicht abonnierte Topics unterscheiden > deferred durchsuchen, ob bereits angelegt
        let subscribed = false
        calendarResponseData?.deferredPosts.map((deferred: DeferredPost) => {
          if (deferred.templateId === templateId) {
            subscribed = true
          }
        })
        if (!dateObject[dateString]) {
          dateObject[dateString] = {}
        }

        // Filter displayType
        if ((showSubscribedPosts && subscribed) || (showMorePosts && !subscribed) || user?.role === 'EDITOR') {
          // Filter nach Types
          if ((filter.types && template.postContent && getPostType(template.postContent) === filter.types.key) || !filter.types) {
            // Filter nach NetworkTypes
            if ((filter.networks && template.allowedNetworks.find(elem => elem === filter.networks?.key)) || !filter.networks) {
              // Filter nach Topics
              if ((filter.topics && template.topicId === filter.topics.key) || !filter.topics) {
                if (templateId) {
                  if (!dateObject[dateString][templateId]) {
                    dateObject[dateString][templateId] = {
                      published: [],
                      deferred: [],
                    }
                  }
                  if (template.active) {
                    dateObject[dateString][templateId].template = template
                    dateObject[dateString][templateId].postContent = template.postContent
                  }
                }
              }
            }
          }
        }
      }
    })
    return dateObject
  }

  const handleLoading = (value: boolean) => {
    setLoading(value)
  }

  useEffect(() => {
    dispatchGetTopics()
    dispatchGetAboSettings()
  }, [])

  // useEffect(() => {
  //   if (location?.state?.detail && filteredCalendarData) {
  //     const index = filteredCalendarData.findIndex((data) => data.id === location.state.detail.templateId)
  //     index !== -1 && setSelectedDate(filteredCalendarData[index].postStart)
  //   }
  // }, [filteredCalendarData])

  useEffect(() => {
    calendarDateParams && dispatchGetCalendarData({ start: calendarDateParams.from, end: calendarDateParams.to })
  }, [calendarDateParams])

  useEffect(() => {
    if (calendarResponseData) {
      const dateObject: DateObject = filterDateObjects()
      setCalendarData(dateObject)
      extractFilterData(dateObject)
    }
  }, [calendarResponseData, topicResponseData, user, filter, showSubscribedPosts, showOwnPosts, showMorePosts])

  return (
    <Grid
      container
      direction="row"
    >
      <Grid container item xs={12}>
        <Paper className={classes.paper}>
          <Grid container item xs={12} spacing={2}>
            <Grid item>
              {topicData &&
                <DropDownComponent
                  options={topicData}
                  disabled={(topicData.length === 0 || !showSubscribedPosts) && user?.role === 'USER'}
                  label={t('coreCalendar:topics')}
                  size="small"
                  value={filter.topics?.key}
                  placeholder={t('coreCalendar:choose')}
                  onChange={(option: DropDownElement) => handleSetFilterOption(option, 'topics')}
                />}
            </Grid>
            <Grid item>
              <DropDownComponent
                options={networkData || []}
                label={t('coreCalendar:networks')}
                size="small"
                placeholder={t('coreCalendar:choose')}
                onChange={(option: DropDownElement) => handleSetFilterOption(option, 'networks')}
              />
            </Grid>
            <Grid item>
              <DropDownComponent
                options={postingTypeData || []}
                label={t('coreCalendar:postType')}
                size="small"
                placeholder={t('coreCalendar:choose')}
                onChange={(option: DropDownElement) => handleSetFilterOption(option, 'types')}
              />
            </Grid>
            {user?.role === 'USER' && (
              <Grid item>
                <DropDownComponent
                  options={displayFilterData}
                  label={t('coreCalendar:displayFilter')}
                  multiple
                  size="small"
                  value={filter.display || undefined}
                  placeholder={t('coreCalendar:choose')}
                  onChange={(option: DropDownElement) => {
                    handleSetFilterOption(option, 'display')
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
      <Grid container item xs={12}>
        <PostCalendarNav
          loading={loading}
          setLoading={handleLoading}
          selectedDate={selectedDate}
          changeSelectedDate={(date: number) => setSelectedDate(date)}
        />
      </Grid>
      <Grid container item xs={12} style={{ marginBottom: theme.spacing(8) }}>
        <Grid item xs={12}>
          <PostCalendar
            loading={loading}
            setLoading={handleLoading}
            selectedDate={selectedDate}
            calendarData={calendarData}
            changeCalendarData={(data: DateObject) => setCalendarData(data)}
            changeSelectedDate={(date: number) => setSelectedDate(date)}
            changeDataParams={(params: CalendarDateParams) => setCalendarDateParams(params)}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
export default ProfessocialCalendar
